/**
 * ServerWorkspacesList — Sous-table des workspaces hébergés sur un serveur.
 *
 * - Stats top (mini cards total/running/stopped/failed).
 * - Barre de progression du quota (amber >80%, red =100%).
 * - Table AntD avec lien vers propriétaire et projet.
 *
 * @spec specs/app/admin/dedicated-servers.md §6.9 §5.5
 */

import { useList } from '@refinedev/core';
import { Progress, Table, Tag } from 'antd';
import { Activity, CheckCircle2, Server, XCircle } from 'lucide-react';
import type { FC } from 'react';
import type { DedicatedServer, ServerWorkspace, WorkspaceStatus } from '@/types/dedicatedServer';
import { SERVER_PLAN_SPECS } from '@/types/dedicatedServer';

interface ServerWorkspacesListProps {
  server: DedicatedServer;
}

const STATUS_TAG: Record<WorkspaceStatus, { color: string; label: string }> = {
  running: { color: 'green', label: 'En cours' },
  stopped: { color: 'default', label: 'Arrêté' },
  starting: { color: 'blue', label: 'Démarrage' },
  failed: { color: 'red', label: 'Échoué' },
  destroyed: { color: 'volcano', label: 'Détruit' },
};

const formatRelative = (iso: string): string => {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  if (Number.isNaN(diff)) return '—';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.floor(hours / 24)} j`;
};

const MiniStat: FC<{
  label: string;
  value: number | string;
  icon: FC<{ className?: string }>;
  color: string;
}> = ({ label, value, icon: Icon, color }) => (
  <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
    <div className={`flex h-8 w-8 items-center justify-center rounded-md ${color}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="text-lg font-bold text-slate-900 dark:text-white">{value}</p>
    </div>
  </div>
);

const ServerWorkspacesList: FC<ServerWorkspacesListProps> = ({ server }) => {
  const { data, isLoading } = useList<ServerWorkspace>({
    resource: `admin/dedicated-servers/${server.id}/workspaces`,
    pagination: { pageSize: 50 },
    queryOptions: { retry: 0 },
  });

  const workspaces = data?.data ?? [];
  const total = workspaces.length;
  const running = workspaces.filter((w) => w.status === 'running').length;
  const stopped = workspaces.filter((w) => w.status === 'stopped').length;
  const failed = workspaces.filter((w) => w.status === 'failed').length;

  const maxWorkspaces = SERVER_PLAN_SPECS[server.plan]?.maxWorkspaces ?? -1;
  const unlimited = maxWorkspaces === -1;
  const quotaPct = unlimited ? 0 : Math.min(100, Math.round((total / maxWorkspaces) * 100));
  const quotaStrokeColor =
    quotaPct >= 100 ? '#dc2626' : quotaPct > 80 ? '#f59e0b' : '#10b981';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MiniStat
          label="Total"
          value={total}
          icon={Server}
          color="bg-blue-100 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300"
        />
        <MiniStat
          label="Actifs"
          value={running}
          icon={Activity}
          color="bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300"
        />
        <MiniStat
          label="Arrêtés"
          value={stopped}
          icon={CheckCircle2}
          color="bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
        />
        <MiniStat
          label="Échoués"
          value={failed}
          icon={XCircle}
          color="bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-300"
        />
      </div>

      <div className="fc-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Quota</h3>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {unlimited ? `${total} / illimité` : `${total} / ${maxWorkspaces}`}
          </span>
        </div>
        {!unlimited && (
          <Progress
            percent={quotaPct}
            strokeColor={quotaStrokeColor}
            status={quotaPct >= 100 ? 'exception' : quotaPct > 80 ? 'active' : 'normal'}
          />
        )}
        {unlimited && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Plan Enterprise : pas de quota fixe.
          </p>
        )}
      </div>

      <div className="fc-card overflow-hidden p-0">
        <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Workspaces hébergés
          </h3>
        </div>
        <Table<ServerWorkspace>
          dataSource={workspaces}
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="middle"
          scroll={{ x: 1000 }}
        >
          <Table.Column<ServerWorkspace> dataIndex="name" title="Nom" />
          <Table.Column<ServerWorkspace>
            dataIndex={['owner', 'email']}
            title="Propriétaire"
            render={(_: unknown, r) =>
              r.owner ? (
                <a
                  href={`/support/users/${r.owner.id}`}
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  {r.owner.email}
                </a>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
          />
          <Table.Column<ServerWorkspace>
            dataIndex={['project', 'name']}
            title="Projet"
            render={(_: unknown, r) =>
              r.project ? (
                <a
                  href={
                    r.project.organizationId
                      ? `/ops/organizations/${r.project.organizationId}`
                      : '#'
                  }
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  {r.project.name}
                </a>
              ) : (
                <span className="text-slate-400">—</span>
              )
            }
          />
          <Table.Column<ServerWorkspace>
            dataIndex="status"
            title="Statut"
            render={(status: WorkspaceStatus) => {
              const cfg = STATUS_TAG[status] ?? { color: 'default', label: status };
              return <Tag color={cfg.color}>{cfg.label}</Tag>;
            }}
          />
          <Table.Column<ServerWorkspace>
            dataIndex="created_at"
            title="Créé"
            render={(iso: string) => (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {formatRelative(iso)}
              </span>
            )}
          />
          <Table.Column<ServerWorkspace>
            dataIndex="last_activity_at"
            title="Dernière activité"
            render={(iso: string) => (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {formatRelative(iso)}
              </span>
            )}
          />
        </Table>
      </div>
    </div>
  );
};

export default ServerWorkspacesList;
